import React, { useState } from "react";

import { useAppContext } from "@components/AppContext";
import { UNPROCESSIBLE_CONDITION_ERROR } from "@api";
import {
  connectSourceOperation,
  readSourceLinkOperation,
} from "@api/services/transactions";
import type { AppContext } from "@components/AppContext/types";

import PlaidConnect from "./PlaidConnect";

type Source = {
  id: string;
  [key: string]: unknown;
};

type SetLoading = (isLoading: boolean) => void;

type ModalHandle = {
  current: {
    open?: (setLoading: SetLoading) => Promise<unknown>;
  };
};

type PlaidAppContext = AppContext & {
  addIdentityCustomerSource: (customerId: string, source: Source) => void;
};

type RequestException = {
  isExpected?: boolean;
  originalError?: Error;
};

type LinkResponse = {
  data: { linkToken: string };
};

type SourceResponse = {
  data: Source;
};

type Props = {
  modal: ModalHandle;
  onClose: () => void;
  onSuccess: (sourceId: string, source: Source) => unknown;
  customerId: string;
  onConnectPlaidSelected?: (callback: () => Promise<void>) => unknown;
};

const PlaidModal = ({
  onSuccess: onSuccessCustom,
  modal,
  onClose,
  customerId,
  onConnectPlaidSelected = undefined,
}: Props): JSX.Element | null => {
  const [ linkToken, setLinkToken ] = useState<string | null>();

  const {
    showErrorMessage,
    authenticatedRequest,
    addIdentityCustomerSource,
  } = useAppContext() as PlaidAppContext;

  const createLinkToken = (): Promise<void> => {
    setLinkToken(null);

    const result = authenticatedRequest<LinkResponse>(readSourceLinkOperation)
      .then(({ data }) => setLinkToken(data.linkToken));

    return result;
  };

  modal.current.open = async (setLoading: SetLoading): Promise<unknown> => {
    const callback = async (): Promise<void> => {
      setLoading(true);
      await createLinkToken();
      setLoading(false);
    };

    if (onConnectPlaidSelected) {
      return onConnectPlaidSelected(() => callback());
    }

    return callback();
  };

  const onSuccess = (sourceToken: string): Promise<unknown> => {
    const parameters = {
      mutation: {
        sourceToken,
        customerId,
      },
    };

    const expectedErrors = [ UNPROCESSIBLE_CONDITION_ERROR ];

    const result = authenticatedRequest<SourceResponse>(connectSourceOperation, parameters, expectedErrors)
      .then(({ data: source }) => {
        addIdentityCustomerSource(customerId, source);

        return onSuccessCustom(source.id, source);
      })
      .catch((exception: RequestException) => {
        const { isExpected, originalError } = exception;

        if (isExpected) {
          showErrorMessage(originalError.message);
        }

        throw exception;
      })
      .finally(() => onClose());

    return result;
  };

  if (!linkToken) {
    return null;
  }

  return (
    <PlaidConnect
      token={linkToken}
      onExit={onClose}
      onSuccess={onSuccess}
    />
  );
};

export default PlaidModal;
